import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Plus, X, Sparkles, Loader2, AlertTriangle, History } from "lucide-react";
import CreditCostTag from "../../CreditCostTag";
import { getSipocGuardrails, SIPOC_MINIMUMS, SIPOC_PROCESS_STEP_THRESHOLD, SIPOC_QUALITY_VALID_THRESHOLD } from "./sipoc";
import { SipocDraftReviewModal } from "./SipocDraftReviewModal";

export function SipocTab({
  sipoc,
  saving,
  aiGenerating,
  hasProblemText,
  aiDraftActive,
  sipocWorkspaceState,
  savedNotice,
  suggestionError,
  pendingSuggestion,
  previewOpen,
  setPreviewOpen,
  sipocVersions,
  sipocQuality,
  selectedSections,
  sectionModes,
  setSectionSelected,
  setSectionMode,
  restorePreviousSipocVersion,
  confirmApplySuggestion,
  applySelectedSuggestionSections,
  applySuggestionSection,
  updatePendingSipocItem,
  addPendingSipocItem,
  removePendingSipocItem,
  cancelSipocPreview,
  regenerateSipocSuggestion,
  addSipocItem,
  updateSipocItem,
  removeSipocItem,
  saveSipoc,
  generateSipocSuggestion,
  continueToStakeholders,
}) {
  const categories = [
    { key: "suppliers", label: "Suppliers", placeholder: "Add supplier", hint: "Who provides inputs?" },
    { key: "inputs", label: "Inputs", placeholder: "Add input", hint: "What goes into the process?" },
    { key: "process_steps", label: "Process", placeholder: "Add process step", hint: "High-level steps" },
    { key: "outputs", label: "Outputs", placeholder: "Add output", hint: "What is produced?" },
    { key: "customers", label: "Customers", placeholder: "Add customer", hint: "Who receives output?" },
  ];
  const { warnings, processStepCount, validation } = getSipocGuardrails(sipoc);
  const compactQualityFeedback = (sipocQuality?.feedback || []).slice(0, 2);
  const stateBadgeTone =
    sipocWorkspaceState === "Saved"
      ? "border-border bg-background/60 text-muted-foreground"
      : sipocWorkspaceState === "Modified"
        ? "border-amber-500/25 bg-amber-500/10 text-amber-300"
        : "border-sky-500/25 bg-sky-500/10 text-sky-300";
  const handleSipocInputKeyDown = (event, category, index) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (index !== (sipoc[category]?.length || 0) - 1) return;
    event.preventDefault();
    addSipocItem(category);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-3">
      <div className="flex flex-col gap-3 border-b border-border/60 pb-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-1">
          <h3 className="font-heading text-xl font-semibold leading-none">SIPOC Diagram</h3>
          <p className="text-xs text-muted-foreground" data-testid="sipoc-guidance">
            Build a high-level SIPOC aligned to the project scope. Keep process steps at the scoping level, not a detailed process map. AI suggestions create an editable draft only and do not auto-save.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 xl:justify-end" data-testid="sipoc-action-bar">
          <div className="flex items-center gap-2" data-testid="sipoc-status-group">
            <div className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${stateBadgeTone}`} data-testid="sipoc-source-badge">
              {sipocWorkspaceState}
            </div>
          </div>
          <Button onClick={saveSipoc} disabled={saving} className="min-w-[132px]" data-testid="save-sipoc-btn">
            {saving ? "Saving..." : "Save SIPOC"}
          </Button>
          <Button
            variant="outline"
            onClick={generateSipocSuggestion}
            disabled={!!aiGenerating || !hasProblemText}
            className="gap-2 min-w-[144px]"
            data-testid="ai-suggest-sipoc-btn"
          >
            {aiGenerating === "SIPOC Draft" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Suggest SIPOC
            <CreditCostTag cost={2} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={restorePreviousSipocVersion}
            disabled={!sipocVersions?.length}
            title="Restore previous version"
            data-testid="restore-sipoc-version-btn"
          >
            <History className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background/35 px-4 py-2.5" data-testid="sipoc-summary-bar">
        <div className="grid gap-2.5 xl:grid-cols-[1fr,360px] xl:items-center">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">SIPOC Summary</span>
              {aiDraftActive && (
                <span
                  className="rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300"
                  data-testid="sipoc-ai-disclosure"
                >
                  AI-generated draft applied
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span data-testid="sipoc-version-history">
                {sipocVersions?.length || 0} version{sipocVersions?.length === 1 ? "" : "s"} available
              </span>
              <span data-testid="sipoc-quality-guidance">
                Aim for 5 to {SIPOC_PROCESS_STEP_THRESHOLD} process steps. Keep entries concise, high-level, and operational.
              </span>
            </div>
          </div>

          {sipocQuality && (
            <div className="border-t border-border/60 pt-2 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0" data-testid="sipoc-quality-panel">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground" data-testid="sipoc-quality-score">
                  {sipocQuality.score}/100
                </p>
                <div
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                    sipocQuality.score >= SIPOC_QUALITY_VALID_THRESHOLD
                      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                      : "border-amber-500/25 bg-amber-500/10 text-amber-300"
                  }`}
                  data-testid="sipoc-quality-status"
                >
                  {sipocQuality.score >= SIPOC_QUALITY_VALID_THRESHOLD ? "Valid for Gate" : "Needs Improvement"}
                </div>
              </div>

              <ul className="mt-1.5 space-y-1 text-xs leading-5 text-muted-foreground" data-testid="sipoc-quality-feedback">
                {compactQualityFeedback.map((item, index) => (
                  <li key={`${item}-${index}`}>• {item}</li>
                ))}
              </ul>

              <details className="mt-1.5 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none" data-testid="sipoc-quality-details-toggle">View details</summary>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <p data-testid="sipoc-quality-completeness">Completeness: {sipocQuality.categories?.completeness ?? 0}/100</p>
                  <p data-testid="sipoc-quality-clarity">Clarity: {sipocQuality.categories?.clarity ?? 0}/100</p>
                  <p data-testid="sipoc-quality-alignment">Alignment to problem: {sipocQuality.categories?.alignment_to_problem ?? 0}/100</p>
                  <p data-testid="sipoc-quality-detail">Level of detail: {sipocQuality.categories?.level_of_detail ?? 0}/100</p>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>

      {suggestionError && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3"
          data-testid="sipoc-suggestion-error"
        >
          <p className="text-sm text-foreground">{suggestionError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateSipocSuggestion}
            disabled={aiGenerating === "SIPOC Draft"}
            data-testid="retry-sipoc-suggestion-btn"
          >
            Retry
          </Button>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="space-y-2" data-testid="sipoc-guardrails">
          {warnings.map((warning) => (
            <div
              key={warning.id}
              className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
              data-testid={`sipoc-warning-${warning.id}`}
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p>{warning.message}</p>
            </div>
          ))}
        </div>
      )}

      {!validation.isValid && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 space-y-2" data-testid="sipoc-validation-summary">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">SIPOC Incomplete</p>
          {validation.issues.map((issue, index) => (
            <p key={`${issue}-${index}`} className="text-xs text-amber-100">{issue}</p>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="sipoc-table">
          <thead>
            <tr>
              {categories.map((cat) => (
                <th key={cat.key}>
                  <div className="space-y-0.5">
                    <p>{cat.label}</p>
                    <p className="text-[10px] font-normal leading-4 text-muted-foreground/70">{cat.hint}</p>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {categories.map((cat) => (
                <td key={cat.key} className="min-w-[190px]">
                  <div
                    className={`space-y-2 rounded-lg border p-3 transition-colors ${
                      validation.issuesBySection?.[cat.key]?.length
                        ? "border-amber-500/30 bg-amber-500/5"
                        : "border-border/70 bg-background/35 hover:border-border hover:bg-background/55"
                    }`}
                    data-testid={`sipoc-section-${cat.key}`}
                  >
                    <p className="text-[11px] text-muted-foreground" data-testid={`sipoc-section-status-${cat.key}`}>
                      {validation.sectionStatus?.[cat.key]?.count ?? 0} / {SIPOC_MINIMUMS[cat.key]} minimum
                    </p>

                    {sipoc[cat.key].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={item}
                          onChange={(e) => updateSipocItem(cat.key, index, e.target.value)}
                          onKeyDown={(event) => handleSipocInputKeyDown(event, cat.key, index)}
                          placeholder={cat.placeholder}
                          className="bg-background/80 text-sm leading-5"
                          title={item}
                          data-testid={`sipoc-${cat.key}-${index}`}
                        />
                        {sipoc[cat.key].length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground/70 hover:bg-accent/60 hover:text-foreground"
                            onClick={() => removeSipocItem(cat.key, index)}
                            title="Remove item"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSipocItem(cat.key)}
                      className="w-full justify-center text-xs font-medium text-foreground/85 hover:text-foreground hover:shadow-sm"
                      title="Add new item"
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add
                    </Button>

                    {cat.key === "process_steps" && (
                      <p className="text-[11px] text-muted-foreground" data-testid="sipoc-process-step-count">
                        {processStepCount} high-level step{processStepCount === 1 ? "" : "s"} entered
                      </p>
                    )}

                    {validation.issuesBySection?.[cat.key]?.map((issue, index) => (
                      <p key={`${cat.key}-${index}`} className="text-[11px] text-amber-200" data-testid={`sipoc-section-issue-${cat.key}-${index}`}>
                        {issue}
                      </p>
                    ))}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
        {savedNotice && (
          <span className="mr-auto text-xs font-medium text-emerald-400" data-testid="sipoc-saved-notice">
            {savedNotice}
          </span>
        )}
        <Button variant="outline" onClick={continueToStakeholders} className="min-w-[196px]" data-testid="continue-to-stakeholders-btn">
          Continue to Stakeholders
        </Button>
      </div>

      <SipocDraftReviewModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        pendingSuggestion={pendingSuggestion}
        currentSipoc={sipoc}
        selectedSections={selectedSections}
        sectionModes={sectionModes}
        setSectionSelected={setSectionSelected}
        setSectionMode={setSectionMode}
        confirmApplySuggestion={confirmApplySuggestion}
        applySelectedSuggestionSections={applySelectedSuggestionSections}
        applySuggestionSection={applySuggestionSection}
        updatePendingSipocItem={updatePendingSipocItem}
        addPendingSipocItem={addPendingSipocItem}
        removePendingSipocItem={removePendingSipocItem}
        cancelSipocPreview={cancelSipocPreview}
        regenerateSipocSuggestion={regenerateSipocSuggestion}
        aiGenerating={aiGenerating}
      />
    </div>
  );
}
